import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  List,
  Divider,
  Alert
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Inventory,
  Warning
} from '@mui/icons-material';
import { orderDisplayNumber } from '../utils/orderDisplay';

const ConfirmAvailabilityModal = ({ order, open, onConfirm, onNotAvailable, onCancel }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  // Сбрасываем состояние обработки при смене заказа или открытии/закрытии модального окна
  useEffect(() => {
    if (open && order) {
      console.log(`[ConfirmAvailabilityModal] Открыто для заказа #${order.id}, сброс состояния`);
      setIsProcessing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- сброс только при смене id (полный order обновляется по SSE)
  }, [order?.id, open]);

  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error('Ошибка подтверждения:', error);
      setIsProcessing(false);
    }
  };

  const handleNotAvailable = async () => {
    setIsProcessing(true);
    try {
      await onNotAvailable();
    } catch (error) {
      console.error('Ошибка возврата заказа:', error);
      setIsProcessing(false);
    }
  };

  const handleCancel = async () => {
    setIsProcessing(true);
    try {
      await onCancel();
    } catch (error) {
      console.error('Ошибка отмены заказа:', error);
      setIsProcessing(false);
    }
  };

  if (!order) return null;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <Box
        sx={{
          bgcolor: 'warning.light',
          color: 'warning.contrastText',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box display="flex" alignItems="center">
          <Inventory sx={{ fontSize: 32, mr: 1 }} />
          <Typography variant="h6" component="h2">
            Проверка наличия
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body1" fontWeight="medium">
            Проверьте наличие всех позиций перед подтверждением заказа
          </Typography>
        </Alert>

        <Box mb={2}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Заказ #{orderDisplayNumber(order)}
          </Typography>
          
          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Позиции для проверки:
          </Typography>

          <List dense>
            {order.items && order.items.map((item, index) => {
              const itemName = item.item_name || 'Позиция';
              const sizeName = item.size_name || '';
              const hasAddons = item.addons && item.addons.length > 0;
              
              return (
                <Box
                  key={item.id || index}
                  sx={{
                    py: 1.5,
                    borderBottom: index < order.items.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider'
                  }}
                >
                  {/* Основная позиция */}
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body1" fontWeight="medium">
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          minWidth: '40px',
                          color: 'primary.main',
                          fontWeight: 'bold'
                        }}
                      >
                        {item.quantity || 1}x
                      </Box>
                      {itemName}{sizeName ? ` (${sizeName})` : ''}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {item.price_at_time || 0} ₽
                    </Typography>
                  </Box>
                  
                  {/* Добавки */}
                  {hasAddons && (
                    <Box sx={{ ml: 5, mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Дополнительно:
                      </Typography>
                      {item.addons.map((addon, addonIndex) => (
                        <Box 
                          key={addonIndex} 
                          display="flex" 
                          justifyContent="space-between"
                          sx={{ ml: 1, mb: 0.5 }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            + {addon.addon_name || addon.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            +{addon.price_at_time || 0} ₽
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </List>

          {/* Сервисный сбор */}
          {order.service_commission && order.service_commission > 0 && (
            <Box mt={2} pt={2} borderTop={1} borderColor="divider">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1" color="text.secondary">
                  Сервисный сбор
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {order.service_commission} ₽
                </Typography>
              </Box>
            </Box>
          )}

          {/* Итого */}
          {order.total_amount && (
            <Box mt={order.service_commission ? 1 : 2} pt={order.service_commission ? 2 : 2} borderTop={2} borderColor="divider">
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight="bold">
                  Итого:
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary.main">
                  {order.total_amount} ₽
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1, flexDirection: 'column' }}>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="success"
          size="large"
          fullWidth
          startIcon={<CheckCircle />}
          disabled={isProcessing}
        >
          {isProcessing ? 'Обработка...' : 'Все в наличии - Подтвердить'}
        </Button>
        
        <Button
          onClick={handleNotAvailable}
          variant="outlined"
          color="warning"
          size="large"
          fullWidth
          startIcon={<Warning />}
          disabled={isProcessing}
        >
          Нет в наличии - Вернуть в ожидание
        </Button>

        <Button
          onClick={handleCancel}
          variant="text"
          color="error"
          size="medium"
          fullWidth
          startIcon={<Cancel />}
          disabled={isProcessing}
        >
          Отменить заказ
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmAvailabilityModal;

